import { registerLocaleData } from '@angular/common';
import { HTTP_INTERCEPTORS, provideHttpClient, withInterceptorsFromDi } from '@angular/common/http';
import localeDe from '@angular/common/locales/de';
import localeDeExtra from '@angular/common/locales/extra/de';
import { ErrorHandler, importProvidersFrom, inject, LOCALE_ID, provideAppInitializer, provideZoneChangeDetection } from '@angular/core';
import { LuxonDateAdapter } from '@angular/material-luxon-adapter';
import { DateAdapter, MAT_DATE_FORMATS, MAT_DATE_LOCALE } from '@angular/material/core';
import { BrowserModule } from '@angular/platform-browser';
import { provideRouter, Router } from '@angular/router';
import { provideTranslateService } from '@ngx-translate/core';
import { provideTranslateHttpLoader } from '@ngx-translate/http-loader';
import { withNgxsReduxDevtoolsPlugin } from '@ngxs/devtools-plugin';
import { provideStore } from '@ngxs/store';
import { catchError, map, of, switchMap, take, timer } from 'rxjs';
import { InteractionType, PublicClientApplication } from '@azure/msal-browser';
import {
  MSAL_GUARD_CONFIG,
  MSAL_INSTANCE,
  MSAL_INTERCEPTOR_CONFIG,
  MsalBroadcastService,
  MsalGuard,
  MsalGuardConfiguration,
  MsalInterceptor,
  MsalInterceptorConfiguration,
  MsalService,
} from '@azure/msal-angular';
import { environment } from '../environments/environment';
import { APP_ROUTES } from './app.routes';
import { OrgUnitState } from './shared/states/orgUnits/orgUnit.state';
import { UserState } from './shared/states/user.state';
import { UnauthorizedInterceptor } from './auth/services/unauthorized-interceptor.service';
import { ReleaseNotesState } from './shared/states/releaseNotes/releaseNotes.state';
import { ImpersonationInterceptor } from './auth/services/impersonation-interceptor.service';
import { NewsState } from './shared/states/news/news.state';
import { NotificationsState } from './shared/states/notifications/notifications.state';
import { AuditState } from './shared/states/audit/audit.state';
import { EmployeeState } from './shared/states/employee.state';
import { ScdState } from './shared/states/scd/scd.state';
import { SessionSyncService } from './shared/services/session-sync.service';
import * as Sentry from '@sentry/angular';

export const msalInstanceFactory = () => new PublicClientApplication(environment.msalConfig);
const msalInterceptorConfigFactory = (): MsalInterceptorConfiguration => {
  const protectedResourceMap = new Map<string, string[]>();
  for (const item of environment.apiConfig) {
    protectedResourceMap.set(`${item.uri}*`, item.scopes);
  }
  return {
    interactionType: InteractionType.Redirect,
    protectedResourceMap,
  };
};
export const msalGuardConfigFactory = (): MsalGuardConfiguration => {
  return {
    interactionType: InteractionType.Redirect,
    authRequest: { scopes: [...environment.apiConfig[0].scopes] },
    loginFailedRoute: '/unauthorized',
  };
};

function initializeMsal(msalService: MsalService, router: Router) {
  return () =>
    timer(50).pipe(
      switchMap(() => msalService.handleRedirectObservable()),
      map(() => {
        const accounts = msalService.instance.getAllAccounts();
        if (accounts.length > 0) {
          msalService.instance.setActiveAccount(msalService.instance.getActiveAccount() || accounts[0]);
        }
      }),
      catchError(error => {
        console.error('MSAL init error:', error);
        void router.navigateByUrl('auth/unauthorized');
        return of(undefined);
      }),
      take(1),
    );
}

function initializeLocale() {
  return () => {
    registerLocaleData(localeDe, 'de', localeDeExtra);
  };
}

export const appConfig = {
  providers: [
    importProvidersFrom(BrowserModule),
    provideRouter(APP_ROUTES),
    provideTranslateService({
      loader: provideTranslateHttpLoader({
        prefix: '/assets/i18n/',
        suffix: '.json',
      }),
      fallbackLang: 'en',
      lang: 'en',
    }),
    {
      provide: LOCALE_ID,
      useValue: 'de',
    },
    {
      provide: MSAL_INSTANCE,
      useFactory: msalInstanceFactory,
    },
    {
      provide: MSAL_GUARD_CONFIG,
      useFactory: msalGuardConfigFactory,
    },
    {
      provide: MSAL_INTERCEPTOR_CONFIG,
      useFactory: msalInterceptorConfigFactory,
    },
    MsalService,
    MsalGuard,
    MsalBroadcastService,
    {
      provide: HTTP_INTERCEPTORS,
      useClass: UnauthorizedInterceptor,
      multi: true,
    },
    {
      provide: HTTP_INTERCEPTORS,
      useClass: MsalInterceptor,
      multi: true,
    },
    {
      provide: HTTP_INTERCEPTORS,
      useClass: ImpersonationInterceptor,
      multi: true,
    },
    {
      provide: ErrorHandler,
      useValue: Sentry.createErrorHandler(),
    },
    {
      provide: Sentry.TraceService,
      deps: [Router],
    },
    provideAppInitializer(() => {
      inject(SessionSyncService);
      const initializerFn = initializeMsal(inject(MsalService), inject(Router));
      return initializerFn();
    }),
    provideAppInitializer(() => {
      const initializerFn = (() => initializeLocale())();
      return initializerFn();
    }),
    provideAppInitializer(() => {
      inject(Sentry.TraceService);
    }),
    { provide: MAT_DATE_LOCALE, useValue: 'de-DE' },
    { provide: DateAdapter, useClass: LuxonDateAdapter, deps: [MAT_DATE_LOCALE] },
    {
      provide: MAT_DATE_FORMATS,
      useValue: {
        parse: {
          dateInput: ['dd.MM.yyyy', 'dd/MM/yyyy', 'dd,MM,yyyy'], // to accept different input styles from user
        },
        display: {
          dateInput: 'dd.MM.yyyy', // display format in input field
          monthYearLabel: 'yyyy MMMM',
          dateA11yLabel: 'MMMM d, y', // 'LL',
          monthYearA11yLabel: 'MMMM yyyy',
        },
      },
    },
    provideStore(
      [UserState, OrgUnitState, ReleaseNotesState, NewsState, NotificationsState, AuditState, EmployeeState, ScdState],
      {
        compatibility: {
          strictContentSecurityPolicy: true,
        },
      },
      withNgxsReduxDevtoolsPlugin(),
    ),
    provideHttpClient(withInterceptorsFromDi()),
    provideZoneChangeDetection(),
  ],
};
